'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Milestone {
  id: number;
  year: string;
  title: string;
  description: string;
  icon: string;
}

interface CompanyTimelineProps {
  className?: string;
}

const CompanyTimeline = ({ className = '' }: CompanyTimelineProps) => {
  const [selectedMilestone, setSelectedMilestone] = useState<number>(1);

  const milestones: Milestone[] = [
    {
      id: 1,
      year: '2003',
      title: 'Foundation',
      description: 'Pink Diamond Company Limited established in Dar es Salaam with a vision to revolutionize corporate apparel in Tanzania.',
      icon: 'RocketLaunchIcon'
    },
    {
      id: 2,
      year: '2008',
      title: 'First Major Partnership',
      description: 'Secured partnership with NMB Bank, marking our entry into large-scale institutional uniform solutions.',
      icon: 'BuildingOffice2Icon'
    },
    {
      id: 3,
      year: '2012',
      title: 'Manufacturing Expansion',
      description: 'Opened state-of-the-art production facility with advanced quality control systems and increased capacity.',
      icon: 'CogIcon'
    },
    {
      id: 4,
      year: '2015',
      title: 'Government Sector Entry',
      description: 'Awarded contract with Tanzania Revenue Authority, establishing credibility in government sector.',
      icon: 'ShieldCheckIcon'
    },
    {
      id: 5,
      year: '2018',
      title: 'Aviation Excellence',
      description: 'Became official uniform provider for Air Tanzania Company Limited, showcasing expertise in specialized apparel.',
      icon: 'PaperAirplaneIcon'
    },
    {
      id: 6,
      year: '2021',
      title: 'Quality Certifications',
      description: 'Achieved ISO 9001:2015 certification and multiple industry quality standards recognitions.',
      icon: 'CheckBadgeIcon'
    },
    {
      id: 7,
      year: '2024',
      title: 'Regional Leadership',
      description: 'Expanded operations to serve clients across East Africa, solidifying position as regional leader.',
      icon: 'GlobeAltIcon'
    },
    {
      id: 8,
      year: '2026',
      title: 'Innovation Hub',
      description: 'Launched design innovation center integrating sustainable practices and smart textile technologies.',
      icon: 'LightBulbIcon'
    }
  ];

  const selected = milestones.find(m => m.id === selectedMilestone) || milestones[0];

  return (
    <section className={`py-20 bg-muted ${className}`}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-headline font-bold text-primary mb-4">
            Our Journey of Excellence
          </h2>
          <p className="text-xl text-text-secondary font-body max-w-3xl mx-auto">
            Two decades of growth, innovation, and unwavering commitment to quality.
          </p>
        </div>
        
        <div className="max-w-6xl mx-auto">
          <div className="relative">
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-accent/30"></div>
            
            <div className="space-y-8">
              {milestones.map((milestone, index) => (
                <div
                  key={milestone.id}
                  className={`relative flex items-center ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className={`w-full md:w-5/12 ${index % 2 === 0 ? 'md:text-right md:pr-12' : 'md:text-left md:pl-12'}`}>
                    <button
                      onClick={() => setSelectedMilestone(milestone.id)}
                      className={`w-full p-6 rounded-lg transition-all duration-300 ${
                        selectedMilestone === milestone.id
                          ? 'bg-accent text-accent-foreground shadow-lg scale-105'
                          : 'bg-card hover:bg-accent/10'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-3xl font-headline font-bold">
                          {milestone.year}
                        </span>
                        <Icon
                          name={milestone.icon as any}
                          size={32}
                          variant="solid"
                          className={selectedMilestone === milestone.id ? 'text-accent-foreground' : 'text-accent'}
                        />
                      </div>
                      <h3 className="text-xl font-headline font-semibold mt-2 mb-2">
                        {milestone.title}
                      </h3>
                      <p className="text-sm font-body opacity-90">
                        {milestone.description}
                      </p>
                    </button>
                  </div>
                  
                  <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-6 h-6 rounded-full bg-accent border-4 border-muted"></div>
                  
                  <div className="hidden md:block w-5/12"></div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="mt-12 bg-card p-8 rounded-lg shadow-lg">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center">
                <Icon
                  name={selected.icon as any}
                  size={32}
                  variant="solid"
                  className="text-accent"
                />
              </div>
              <div>
                <span className="text-4xl font-headline font-bold text-primary">
                  {selected.year}
                </span>
                <h3 className="text-2xl font-headline font-semibold text-primary">
                  {selected.title}
                </h3>
              </div>
            </div>
            <p className="text-lg text-text-secondary font-body leading-relaxed">
              {selected.description}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompanyTimeline;